<template>
  <div class="wecom-kf-session">
    <el-form size="small" :inline="true">
      <el-form-item label="时间：">
        <el-date-picker v-model="dateRange" type="daterange" unlink-panels range-separator="至" start-placeholder="开始日期"
          end-placeholder="结束日期" value-format="yyyy-MM-dd" style="width: 340px" />
      </el-form-item>
      <el-form-item label="客服：">
        <el-select v-model="query.servicerUserId" clearable filterable placeholder="全部" style="width: 160px">
          <el-option label="未分配" value="__NONE__" />
          <el-option v-for="item in servicers" :key="item.servicerUserId" :label="item.servicerName"
            :value="item.servicerUserId" />
        </el-select>
      </el-form-item>
      <el-form-item label="超时：">
        <el-select v-model="query.timeout" clearable placeholder="全部" style="width: 110px">
          <el-option label="超时" :value="true" />
          <el-option label="未超时" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态：">
        <el-select v-model="query.status" clearable placeholder="全部" style="width: 120px">
          <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="标签：">
        <el-input v-model="query.tag" clearable placeholder="吐槽标签" style="width: 140px" />
      </el-form-item>
      <el-form-item label="最低分：">
        <el-input-number v-model="query.minScore" :min="1" :max="5" controls-position="right" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" :loading="loading" @click="reload">查询</el-button>
      </el-form-item>
    </el-form>
    <p v-if="query.timeBy === 'end'" class="tip">当前按会话结束时间筛选，与客服考核口径一致。</p>

    <el-table v-loading="loading" :data="records" stripe>
      <el-table-column label="开始时间" width="160">
        <template slot-scope="scope">{{ scope.row.startTime || "—" }}</template>
      </el-table-column>
      <el-table-column label="乘客" min-width="140">
        <template slot-scope="scope">
          <div>{{ scope.row.nickName || "—" }}</div>
          <div v-if="scope.row.telephone" v-click-tel="scope.row.telephone">{{ scope.row.telephone | encryptTel }}</div>
        </template>
      </el-table-column>
      <el-table-column label="客服" min-width="120">
        <template slot-scope="scope">{{ servicerText(scope.row) }}</template>
      </el-table-column>
      <el-table-column label="首响" width="110">
        <template slot-scope="scope">{{ formatDuration(scope.row.firstResponseSeconds) }}</template>
      </el-table-column>
      <el-table-column label="超时" width="70">
        <template slot-scope="scope">{{ scope.row.responseTimeout ? "是" : "否" }}</template>
      </el-table-column>
      <el-table-column label="正确" width="70">
        <template slot-scope="scope">{{ scoreText(scope.row.correctness) }}</template>
      </el-table-column>
      <el-table-column label="规范" width="70">
        <template slot-scope="scope">{{ scoreText(scope.row.ruleCompliance) }}</template>
      </el-table-column>
      <el-table-column label="态度" width="70">
        <template slot-scope="scope">{{ scoreText(scope.row.attitude) }}</template>
      </el-table-column>
      <el-table-column label="认真" width="70">
        <template slot-scope="scope">{{ scoreText(scope.row.thoroughness) }}</template>
      </el-table-column>
      <el-table-column label="标签" min-width="160">
        <template slot-scope="scope">
          <el-tag v-for="tag in scope.row.complaintTags || []" :key="tag" size="mini" class="tag">{{ tag }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template slot-scope="scope">{{ statusLabel(scope.row.status) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="80" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openDetail(scope.row.id)">详情</el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination class="pager" background layout="total, prev, pager, next, sizes" :page-sizes="[10, 20, 50]"
      :current-page.sync="page.current" :page-size.sync="page.size" :total="page.total" @size-change="reload"
      @current-change="load" />

    <el-drawer title="会话详情" :visible.sync="detailVisible" size="80%" append-to-body>
      <div v-loading="detailLoading" class="detail">
        <div class="chat" ref="chat">
          <div v-for="item in messages" :key="item.msgid" :class="['msg-row', rowClass(item)]">
            <div :id="'msg-' + item.msgid" :class="['bubble', bubbleClass(item), { hit: hitId === item.msgid }]">
              <div class="msg-meta">{{ roleLabel(item.origin) }} {{ item.sendAt }}</div>
              <file-preview v-if="item.media" :value="item.media" />
              <div v-else>{{ item.content }}</div>
              <div v-if="item.waitSeconds != null && item.origin === 5" class="wait">等待了 {{
                formatDuration(item.waitSeconds)
              }}</div>
              <div v-if="item.waitSeconds != null && item.origin === 3" class="wait">结束时尚未回复，已等待 {{
                formatDuration(item.waitSeconds) }}</div>
            </div>
          </div>
        </div>
        <div class="review" v-if="session">
          <div class="review-title">{{ session.servicerName || "未分配" }} · {{ statusLabel(session.status) }}</div>
          <p v-if="session.failReason" class="fail">{{ session.failReason }}</p>
          <el-alert v-if="review && review.textOnly === false" type="warning" :closable="false"
            title="本通含未识别的图片或语音，结论仅基于文字" show-icon />
          <template v-if="review">
            <div class="scores">
              <span>正确 {{ scoreText(review.correctness) }}</span>
              <span>规范 {{ scoreText(review.ruleCompliance) }}</span>
              <span>态度 {{ scoreText(review.attitude) }}</span>
              <span>认真 {{ scoreText(review.thoroughness) }}</span>
            </div>
            <p>{{ review.summary }}</p>
            <div v-for="(issue, index) in review.issues || []" :key="index" class="issue">
              <el-tag size="mini">{{ issue.type }}</el-tag>
              <span>{{ issue.summary }}</span>
              <el-button v-for="msgid in issue.evidenceMsgIds || []" :key="msgid" type="text" size="mini"
                @click="locate(msgid)">定位</el-button>
            </div>
            <div class="block-title">改进点</div>
            <div v-for="(item, index) in review.improvementPoints || []" :key="'p' + index">{{ item }}</div>
            <el-form size="small" label-width="70px" class="review-form">
              <el-form-item label="人工">
                <el-radio-group v-model="reviewForm.reviewStatus">
                  <el-radio :label="1">采纳</el-radio>
                  <el-radio :label="2">驳回</el-radio>
                </el-radio-group>
              </el-form-item>
              <el-form-item label="标签">
                <el-select v-model="reviewForm.correctedTags" multiple filterable allow-create default-first-option
                  placeholder="可修改吐槽标签" style="width: 100%">
                  <el-option v-for="tag in reviewForm.correctedTags" :key="tag" :label="tag" :value="tag" />
                </el-select>
              </el-form-item>
              <el-form-item label="备注">
                <el-input v-model="reviewForm.remark" type="textarea" :rows="3" />
              </el-form-item>
              <el-form-item>
                <el-button type="primary" :loading="saving" @click="saveReview">保存核对</el-button>
              </el-form-item>
            </el-form>
          </template>
          <el-empty v-else description="还没有质检结果" />
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { getWecomKfServicers, getWecomKfSessionPage, getWecomKfSessionDetail, saveWecomKfReview } from "@/api/wecom_kf";
import { formatDuration, scoreText } from "@/pages/WecomKf/Insight/index.vue";
import FilePreview from "@/components/FilePreview";

const STATUS_OPTIONS = [
  { value: 0, label: "进行中" },
  { value: 1, label: "待分析" },
  { value: 2, label: "已分析" },
  { value: 3, label: "分析失败" }
];

export default {
  components: { FilePreview },
  props: {
    preset: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      dateRange: [],
      query: {
        servicerUserId: "",
        timeout: null,
        status: null,
        tag: "",
        minScore: undefined,
        timeBy: ""
      },
      servicers: [],
      statusOptions: STATUS_OPTIONS,
      loading: false,
      records: [],
      page: { current: 1, size: 10, total: 0 },
      detailVisible: false,
      detailLoading: false,
      saving: false,
      session: null,
      messages: [],
      review: null,
      hitId: "",
      reviewForm: { reviewStatus: 1, correctedTags: [], remark: "" }
    };
  },
  created() {
    if (this.preset) {
      this.applyPreset(this.preset);
    }
    this.loadServicers();
    this.load();
  },
  watch: {
    preset(value) {
      if (!value) {
        return;
      }
      this.applyPreset(value);
      this.reload();
    }
  },
  methods: {
    formatDuration,
    scoreText,
    applyPreset(preset) {
      this.query.servicerUserId = preset.servicerUserId || "";
      this.query.tag = preset.tag || "";
      this.query.timeBy = preset.timeBy || "";
      if (preset.startDate && preset.endDate) {
        this.dateRange = [preset.startDate, preset.endDate];
      }
    },
    loadServicers() {
      getWecomKfServicers().then((res) => {
        this.servicers = res.data || [];
      });
    },
    reload() {
      this.page.current = 1;
      this.load();
    },
    load() {
      const params = {
        page: this.page.current,
        size: this.page.size
      };
      if (this.query.timeBy) {
        params.timeBy = this.query.timeBy;
      }
      if (this.query.servicerUserId) {
        params.servicerUserId = this.query.servicerUserId;
      }
      if (this.query.tag) {
        params.tag = this.query.tag;
      }
      if (this.query.status != null && this.query.status !== "") {
        params.status = this.query.status;
      }
      if (this.query.timeout === true || this.query.timeout === false) {
        params.timeout = this.query.timeout;
      }
      if (this.query.minScore) {
        params.minScore = this.query.minScore;
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.startDate = this.dateRange[0];
        params.endDate = this.dateRange[1];
      }
      this.loading = true;
      getWecomKfSessionPage(params).then((res) => {
        const data = res.data || {};
        this.records = data.records || [];
        this.page.total = data.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },
    openDetail(id) {
      this.detailVisible = true;
      this.detailLoading = true;
      this.hitId = "";
      getWecomKfSessionDetail({ id }).then((res) => {
        const data = res.data || {};
        this.session = data.session;
        this.messages = data.messages || [];
        this.review = data.review;
        const tags = (data.review && (data.review.correctedTags && data.review.correctedTags.length
          ? data.review.correctedTags : data.review.complaintTags)) || [];
        this.reviewForm = {
          reviewStatus: data.review && data.review.reviewStatus ? data.review.reviewStatus : 1,
          correctedTags: tags.slice(),
          remark: (data.review && data.review.remark) || ""
        };
      }).finally(() => {
        this.detailLoading = false;
      });
    },
    saveReview() {
      this.saving = true;
      saveWecomKfReview({
        sessionId: this.session.id,
        reviewStatus: this.reviewForm.reviewStatus,
        correctedTags: this.reviewForm.correctedTags,
        remark: this.reviewForm.remark
      }).then(() => {
        this.$message.success("已保存");
        this.openDetail(this.session.id);
        this.load();
      }).finally(() => {
        this.saving = false;
      });
    },
    locate(msgid) {
      this.hitId = msgid;
      this.$nextTick(() => {
        const el = document.getElementById("msg-" + msgid);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    servicerText(row) {
      const name = row.servicerName || "未分配";
      if (row.servicerUserIds && row.servicerUserIds.length > 1) {
        return name + " 等";
      }
      return name;
    },
    statusLabel(status) {
      const found = STATUS_OPTIONS.find((item) => item.value === status);
      return found ? found.label : "—";
    },
    roleLabel(origin) {
      if (origin === 3) return "客户";
      if (origin === 5) return "客服";
      if (origin === 6) return "系统";
      return "事件";
    },
    rowClass(item) {
      if (item.origin === 5) return "is-servicer";
      if (item.origin === 3) return "is-customer";
      return "is-system";
    },
    bubbleClass(item) {
      if (item.origin === 5) return "servicer";
      if (item.origin === 3) return "customer";
      return "system";
    }
  }
};
</script>

<style scoped>
.tip {
  color: #909399;
  font-size: 13px;
  margin-top: 0;
}

.pager {
  margin-top: 12px;
  text-align: right;
}

.tag {
  margin-right: 4px;
}

.detail {
  display: flex;
  height: calc(100vh - 120px);
  padding: 0 16px 16px;
}

.chat {
  flex: 1;
  overflow: auto;
  background: #f5f7fa;
  padding: 12px;
}

.review {
  width: 360px;
  margin-left: 16px;
  overflow: auto;
}

.msg-row {
  display: flex;
  margin-bottom: 10px;
}

.is-servicer {
  justify-content: flex-end;
}

.is-system {
  justify-content: center;
}

.bubble {
  max-width: 70%;
  padding: 8px 12px;
  border-radius: 6px;
  background: #fff;
}

.servicer {
  background: #d9ecff;
}

.system {
  background: transparent;
  color: #909399;
  font-size: 12px;
}

.msg-meta {
  color: #909399;
  font-size: 12px;
  margin-bottom: 4px;
}

.wait {
  color: #e6a23c;
  font-size: 12px;
  margin-top: 4px;
}

.hit {
  outline: 2px solid #e6a23c;
}

.review-title {
  font-weight: 600;
  margin-bottom: 8px;
}

.fail {
  color: #f56c6c;
  font-size: 13px;
}

.scores span {
  margin-right: 12px;
}

.issue {
  margin: 6px 0;
}

.block-title {
  margin-top: 12px;
  font-weight: 600;
}

.review-form {
  margin-top: 16px;
}
</style>
